import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
  Typography,
  Collapse,
} from '@mui/material';
import {
  FaTachometerAlt,
  FaIdBadge,
  FaUserCircle,
  FaCalendarAlt,
  FaBoxes,
  FaClipboardList,
  FaMoneyBillWave,
  FaUserFriends,
  FaChevronDown,
  FaChevronUp,
  FaChartLine,
  FaCog,
} from 'react-icons/fa';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../../assets/logo.svg';

function Sidebar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();

  // Grouped menu items
  const menuGroups = [
    {
      title: 'MAIN',
      icon: <FaTachometerAlt />,
      items: [
        { text: 'Dashboard', icon: <FaTachometerAlt />, link: '/Dashboard_gm' },
      ]
    },
    {
      title: 'Employees',
      icon: <FaUserFriends />,
      items: [
        { text: 'My Staff', icon: <FaUserFriends />, link: '/StaffDirectory' },
        { text: 'Staff Tasks', icon: <FaUserCircle />, link: '/TaskManagementDashboard' },
      ]
    },
    {
      title: 'Store Keeping',
      icon: <FaBoxes />,
      items: [
        { text: 'Inventory', icon: <FaBoxes />, link: '/GMInventoryDashboard' },
        { text: 'Requisitions', icon: <FaClipboardList />, link: '/GMRequisitionApproval' },
 { text: 'Purshase Order', icon: <FaClipboardList />, link: '/PurshaseOrdeGMApproval' },

        
      ]
    },
    {  
      title: 'SALARY',
      icon: <FaMoneyBillWave />,
      items: [
        { text: 'Salaries & Approvals', icon: <FaMoneyBillWave />, link: '/SalaryApprovals' },
          { text: 'Salary Deductions', icon: <FaMoneyBillWave />, link: '/GmDeductionDashboard' },
           { text: 'Payroll', icon: <FaMoneyBillWave />, link: '/PayrollGenerator' },
      ]
    },
    {
      title: 'LEAVE MANAGEMENT',
      icon: <FaCalendarAlt />,
      items: [
        { text: 'Leave Request', icon: <FaCalendarAlt />, link: '/LeaveCalendar_gm' },
      ]
    },
    {
      title: 'ACCOUNT SETTINGS',
      icon: <FaCog />,
      items: [
        { text: 'Change Password', icon: <FaIdBadge />, link: '/passwordChange_GM' },
      ]
    }
  ];

  const toggleDrawer = () => setDrawerOpen(!drawerOpen);
  const handleMenuItemClick = () => {
    if (window.innerWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const toggleMenuGroup = (groupTitle) => {
    setOpenMenus(prev => ({
      ...prev,
      [groupTitle]: !prev[groupTitle]
    }));
  };

  // Check if any item in a group is active
  const isGroupActive = (group) => {
    return group.items.some(item => location.pathname === item.link);
  };

  // Auto-expand groups that contain the active link
  React.useEffect(() => {
    const newOpenMenus = { ...openMenus };
    menuGroups.forEach(group => {
      const hasActive = group.items.some(item => location.pathname === item.link);
      if (hasActive && !newOpenMenus[group.title]) {
        newOpenMenus[group.title] = true;
      }
    });
    setOpenMenus(newOpenMenus);
  }, [location.pathname]);

  const renderMenuItem = (item) => {
    const isSelected = location.pathname === item.link;
    return (
      <ListItem
        button
        component={Link}
        to={item.link}
        key={item.text}
        onClick={handleMenuItemClick}
        sx={{
          backgroundColor: isSelected ? '#37474F' : 'transparent',
          boxShadow: isSelected ? '0px 4px 12px rgba(0, 0, 0, 0.4)' : 'none',
          border: isSelected ? '1px solid #455A64' : 'none',
          borderRadius: '12px',
          paddingY: 1.5,
          paddingX: 2,
          marginY: 0.5,
          marginX: 1,
          color: '#ECEFF1',
          '&:hover': {
            backgroundColor: '#455A64',
            boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.4)',
          },
          pl: 4, // Indent sub-items
        }}
      >
        <Box sx={{ marginRight: 2, fontSize: '1.2rem' }}>{item.icon}</Box>
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            fontSize: '0.9rem',
            fontWeight: isSelected ? 600 : 400,
          }}
        />
      </ListItem>
    );
  };

  const renderMenuGroup = (group) => {
    const isOpen = openMenus[group.title];
    const isActive = isGroupActive(group);
    
    return (
      <Box key={group.title}>
        <ListItem
          button
          onClick={() => toggleMenuGroup(group.title)}
          sx={{
            backgroundColor: isActive ? '#37474F' : '#263238',
            borderRadius: '12px',
            paddingY: 1.5,
            paddingX: 2,
            marginY: 0.5,
            marginX: 1,
            color: '#ECEFF1',
            '&:hover': {
              backgroundColor: '#455A64',
            },
          }}
        >
          <Box sx={{ marginRight: 2, fontSize: '1.2rem' }}>{group.icon}</Box>
          <ListItemText
            primary={group.title}
            primaryTypographyProps={{
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}
          />
          {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {group.items.map(item => renderMenuItem(item))}
          </List>
        </Collapse>
      </Box>
    );
  };

  const renderMenuItems = () => (
    <List>
      {menuGroups.map((group) => renderMenuGroup(group))}
    </List>
  );

  return (
    <>
      {/* Mobile menu button */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1301,
          display: { xs: 'block', sm: 'none' },
        }}
      >
        <IconButton color="inherit" onClick={toggleDrawer} aria-label="Open menu">
          <MenuIcon sx={{ color: 'white' }} />
        </IconButton>
      </Box>

      {/* Drawer for mobile */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: 260,
            backgroundColor: '#263238',
            color: 'white',
          },
        }}
      >
        <Box
          sx={{
            width: 260,
            height: '100%',
            backgroundColor: '#263238',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              textAlign: 'center',
              paddingY: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
            }}
          >
            <img src={logo} alt="Logo" style={{ width: 80, height: 'auto' }} />
          </Box>
          <Divider sx={{ borderColor: '#455A64', marginY: 2 }} />
          {renderMenuItems()}
        </Box>
      </Drawer>

      {/* Sidebar for desktop */}
      <Box
        sx={{
          width: 260,
          height: '100vh',
          backgroundColor: '#263238',
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          color: 'white',
          position: 'sticky',
          top: 0,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#1e2a2f',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#455A64',
            borderRadius: '4px',
          },
        }}
      >
        {/* Logo and title */}
        <Box
          sx={{
            width: '100%',
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            mb: 2,
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: 100,
              height: 'auto',
            }}
          />
        </Box>
        {renderMenuItems()}
        
        {/* Footer */}
        <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#78909C', fontSize: '0.7rem' }}>
            © Elegantè HRMS
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default Sidebar;